import {Component, Prop, h, Listen, Element, Event, type EventEmitter} from '@stencil/core';
import type { Strategies } from '../../../../../../strategies/Strategies';
import type { ResultRenderer } from '../../../../../../../utils/Renderer';
import { DashboardCarouselItem } from './dashboard-carousel-item/dashboard-carousel-item';
import type { SearchProduct } from '../../../../../../../api/SearchApi';
import { DashboardItemHeader } from '../../dashboard-item-header/dashboard-item-header';
import {EolDiscontinuedResultList} from '../../../eol-discontinued-result-list/eol-discontinued-result-list';
import type {IEolDiscontinuedDetails} from '../../../types/subcomponents-types.type';
import type { labelProviderInterface } from '../../../../../../LabelProviderInterface';
import type {MiaFiltersDetails} from '../../../../../../widgets/filter-related-widget/additional-conditions-banner/types';
import type {WCEventData} from '../../../../../../../api/model/response/documents/SearchResult';

@Component({
  tag: 'uiaas-search-dashboard-carousel',
  shadow: true,
  styleUrl: 'uiaas-search-dashboard-carousel.scss',
})
export class DashboardCarousel {
  @Prop() readonly country: string;
  @Prop() readonly language: string;
  @Prop() readonly products: Array<SearchProduct>;
  @Prop() readonly tabName: string;
  @Prop() readonly labelProvider: labelProviderInterface;
  @Prop() readonly strategies: Strategies;
  @Prop() readonly resultRenderer: ResultRenderer;
  @Prop() readonly resultClickAnalytics: CallableFunction;
  @Prop() readonly isEcommerceMode: boolean;
  @Prop() readonly eShop: boolean;
  @Prop() readonly rtl: boolean;
  @Prop() readonly tabCount: number;
  @Prop() readonly targetTab: string;
  @Prop() readonly subTitle: boolean = false;
  @Prop() readonly brand?: string;
  @Prop() readonly term: string;
  @Prop() readonly lazyLoadImages?:boolean;
  @Prop() readonly isSubstitutionTool?: boolean;
  @Prop() readonly eolDiscontinuedResults?: IEolDiscontinuedDetails;
  @Prop() readonly isAdditionalConditionsWidgetActive?: boolean;
  @Prop() readonly isMiaPromotionWidget?: boolean;
  @Prop() readonly miaData?: MiaFiltersDetails;
  @Prop() readonly isMySeVersion?: boolean;
  @Prop() readonly wcEventData?: WCEventData;
  @Prop() readonly isUnifiedCheckout?: boolean;
  @Prop() readonly priceBanner?: boolean;

  @Element() element;

  private carouselContent;


  @Event({
    eventName: 'uiaas:carousel-scrolled',
    composed: true,
    cancelable: true,
    bubbles: true
  })
    carouselScrolled:EventEmitter;

  private handleCarouselScroll () {
      this.carouselScrolled.emit();
  }

  componentDidLoad() {
      const carousel = this.element.shadowRoot.querySelector('se-carousel');
      this.carouselContent = carousel?.shadowRoot?.querySelector('.content');
      this.carouselContent?.addEventListener('scroll', this.handleCarouselScroll.bind(this));
  }

  disconnectedCallback () {
      this.carouselContent?.removeEventListener('scroll', this.handleCarouselScroll);
  }


  render() {
      const resultIndexOffset = this.eolDiscontinuedResults?.documents?.length || 0;
      return (
        <div class="dashboard-view-carousel" dir={this.rtl ? 'rtl' : void (0)}>
          <DashboardItemHeader
            title={this.labelProvider(`tab.${this.tabName}`, 'Products')}
            labelProvider={(e, f) => this.labelProvider(e, f)}
            tabCount={this.tabCount}
            targetTab={this.targetTab}
            subTitle={this.subTitle}
            privateCard={false}
            itemCount={this.products.length}
            term={this.term}
          />
          <uiaas-search-widget-additional-conditions-banner
            isProductTabVariant={false}
            labelProvider={(e, f) => this.labelProvider(e, f)}
            term={this.term}
            isAdditionalConditionsWidgetActive={this.isAdditionalConditionsWidgetActive}
            isMiaPromotionWidget={this.isMiaPromotionWidget}
            miaData={this.miaData}
            isMySeVersion={this.isMySeVersion}
          >
          </uiaas-search-widget-additional-conditions-banner>
          {this.priceBanner ?
            <uiaas-search-widget-cta-banner
              slot="before-results"
              url="/myschneider/priceAndAvailability"
              fluid={true}
              label={this.labelProvider(
                'redirection.banner.widget.paTool',
                'Please refer to our <b>Price and Availability tool</b> ' +
                'for quick, precise insights into product pricing and availability.')}
              heading={this.labelProvider('redirection.banner.widget.paHeading', 'Discover product pricing and availability!')}
              cta={this.labelProvider('redirection.banner.widget.cta', 'Explore')}
              icon="box2"
            />
            : ''}
          <div class="eol-result-list">
            <EolDiscontinuedResultList
              loading={false}
              tabular={false}
              mobileView={false}
              isSubstitutionTool={this.isSubstitutionTool}
              eolDiscontinuedResults={this.eolDiscontinuedResults}
              labelProvider={this.labelProvider}
              tabName={this.tabName}
              strategies={this.strategies}
              rtl={this.rtl}
              term={this.term}
              language={this.language}
              country={this.country}
            />
          </div>
          <qds-web-carousel indicator-position={'bottom-end'} itemMinWidth={240}>
            <DashboardCarouselItem
              strategies={this.strategies}
              resultRenderer={this.resultRenderer}
              resultClickAnalytics={this.resultClickAnalytics}
              products={this.products}
              labelProvider={(e, f) => this.labelProvider(e, f)}
              country={this.country}
              language={this.language}
              isEcommerceMode={this.isEcommerceMode}
              lazyLoadImages={this.lazyLoadImages}
              eShop={this.eShop}
              rtl={this.rtl}
              resultIndexOffset={resultIndexOffset}
              isSubstitutionTool={this.isSubstitutionTool}
              wcEventData={this.wcEventData}
              isUnifiedCheckout={this.isUnifiedCheckout}
            />
          </qds-web-carousel>
        </div>
      );
  }
}
